import { useLayoutEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';

// Shared detail-row primitive. Labels are truncated with ellipsis (CSS)
// and gain a native-tooltip title attribute *only* when truncation
// actually happens — detected at runtime via scrollWidth > clientWidth
// and re-checked on resize. Plain-string-labelled rows get the
// behaviour automatically; rows with JSX in the label slot (rare —
// e.g. PO detail's Receipts/Invoices cards) still rely on the inner
// element's own hover affordances.

interface DetailRowProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly mono?: boolean;
}

export function DetailRow({ label, value, mono = false }: DetailRowProps): JSX.Element {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el) return;
    const check = (): void => {
      setTruncated(el.scrollWidth > el.clientWidth);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [label]);

  return (
    <div className="detail-row">
      <span
        ref={labelRef}
        className="detail-row-label"
        {...(truncated ? { title: label } : {})}
      >
        {label}
      </span>
      <span className={`detail-row-value${mono ? ' mono' : ''}`}>{value}</span>
    </div>
  );
}
